import { ChevronRightIcon } from "@heroicons/react/solid";
import {
  compact,
  find,
  flatten,
  get,
  groupBy,
  head,
  isEmpty,
  last,
  map,
  mean,
  meanBy,
  orderBy,
  pick,
  reduce,
  sum,
  sumBy,
  values,
  zipWith
} from "lodash";
import moment from "moment";
import { stringifyUrl } from "query-string";
import Tooltip from "rc-tooltip";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Bar,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip
} from "recharts";
import { useHeliumData } from "../context/heliumData";
import { HotspotMakerAddressKey } from "../context/hotspotMakers/HotspotMakersProvider";
import { useHotspots } from "../context/hotspots";
import {
  Data,
  DataSummary,
  defaultSummary
} from "../context/hotspots/HotspotsProvider";
import { useInstalls } from "../context/installs";
import { useLocations } from "../context/locations";
import { useStats } from "../context/stats";
import {
  calculateIsInteractiveHotspot,
  useTransmitScales
} from "../context/transmitScales/TransmitScalesProvider";
import { useViewport } from "../context/viewport";
import { useWorkspaces } from "../context/workspaces/WorkspacesProvider";
import { rewardTypes } from "../pages/Map/HotspotDetails/HotspotRewardsTab";
import { isActiveInstall } from "../pages/Workspace/InstallsData/utils";
import "../styles/snap-scroll.css";
import classNames from "../utils/classNames";
import { avatarsApi, breakpoints } from "../utils/constants";
import {
  CURRENT_CALENDAR_MONTH_DISPLAY,
  PREVIOUS_CALENDAR_MONTH_DISPLAY
} from "../utils/dates";
import {
  ClassificationValue,
  formatHotspotName,
  formatHotspotPlace,
  getClassification
} from "../utils/hotspotUtils";
import { pointDistanceMemoized } from "../utils/memoizedMethods";
import { Hotspot, isPhpHotspot, PhpHotspot } from "../utils/types";
import { useHntMarket } from "../utils/useHntMarket";
import useMapConfig from "../utils/useMapConfig";
import usePrevious from "../utils/usePrevious";
import AlertMessage from "./AlertMessage";
import CenteredLoading from "./CenteredLoading";
import DonutChart from "./charts/DonutChart";
import HistoricalDataChartTooltipWithCurrency from "./charts/HistoricalDataChartTooltipWithCurrency";
import DataPeriodSelector from "./DataPeriodSelector";
import DotsFallback from "./DotsFallback";
import ExternalLink from "./ExternalLink";
import InfoIcon from "./icons/InfoIcon";
import DisplayLabels from "./labels/DisplayLabels";
import NavLink from "./NavLink";
import PercentageChange, {
  calculatePercentChange,
  formatPercentChangeString
} from "./PercentageChange";
import StatusDot from "./StatusDot";

interface Props {
  hotspots: Hotspot[];
  gridColsInDesktop?: number;
  withExtraRewardSummaries?: boolean; // These reward summary fields are not available on the hex-based endpoint
  withRewardsByType?: boolean;
  historyRewardsWithoutToday?: boolean;
}

const Dashboard: React.FC<Props> = ({
  hotspots,
  gridColsInDesktop = 2,
  withExtraRewardSummaries = false,
  withRewardsByType = false,
  historyRewardsWithoutToday = true
}) => {
  const { width } = useViewport();
  const { displayInCurrency } = useHntMarket();
  const {
    hotspotRewardsSummary,
    hotspotRewardsHistory,
    hotspotWitnessesCountsMap,
    fetchRewardsHistory,
    hotspotRewardsByTypeMap,
    getRewardsHistoryChartData,
    getHotspotCoordinates
  } = useHotspots();
  const { installs } = useInstalls();
  const { locations } = useLocations();
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const history = useHistory();
  const [showAll, setShowAll] = useState<{ [key: string]: boolean }>({});
  const isMobile = useMemo(() => width < breakpoints.md, [width]);
  const { chainVars } = useHeliumData();
  const { stats } = useStats();
  const { isTransmitScaleActive } = useTransmitScales();
  const fetchedRewards = useRef(false);
  const { currentWorkspace } = useWorkspaces();

  const hip15WitnessRedundancy = useMemo(
    () => get(chainVars, "witnessRedundancy", 4),
    [chainVars]
  );

  const hotspotsByWorstTransmitScale = useMemo(
    () =>
      orderBy(
        hotspots.filter(
          (hotspot) => hotspot.reward_scale > 0 && hotspot.reward_scale < 1
        ),
        "reward_scale",
        "asc"
      ),
    [hotspots]
  );

  const relevantRewardsByType = useMemo(
    () => pick(hotspotRewardsByTypeMap, map(hotspots, "id")),
    [hotspotRewardsByTypeMap, hotspots]
  );

  const rewardsByTypeFirstTimestamp = useMemo(
    () =>
      head(
        compact(
          map(values(relevantRewardsByType), "first_reward_timestamp")
        ).sort()
      ),
    [relevantRewardsByType]
  );

  const rewardsByTypeLastTimestamp = useMemo(
    () =>
      last(
        compact(
          map(values(relevantRewardsByType), "last_reward_timestamp")
        ).sort()
      ),
    [relevantRewardsByType]
  );

  const rewardAmountsByType = useMemo(() => {
    const allRewardsByType = flatten(
      map(values(relevantRewardsByType), "rewards")
    );

    return reduce(
      groupBy(allRewardsByType, "type"),
      (
        acc: { type: string; amount: number }[],
        values: { type: string; amount: number; percentage: number }[],
        type: string
      ) => {
        acc.push({ type, amount: sumBy(values, "amount") });
        return acc;
      },
      []
    );
  }, [relevantRewardsByType]);

  const rewardsByTypeChartData = useMemo(() => {
    if (isEmpty(hotspotRewardsByTypeMap)) return [];

    const totalAmount = sumBy(rewardAmountsByType, "amount");

    return orderBy(rewardAmountsByType, "amount", "desc")
      .filter(({ amount }) => amount)
      .map(({ type, amount }) => ({
        key: type,
        name: get(rewardTypes, [type, "name"], type.replace("_", " ")),
        value: amount,
        displayValues: [
          displayInCurrency(amount, {
            needsDivision: true,
            withHntSuffix: true
          })
        ],
        percentage: (amount / totalAmount) * 100,
        color: get(rewardTypes, [type, "color"])
      }));
  }, [displayInCurrency, hotspotRewardsByTypeMap, rewardAmountsByType]);

  const rewardReductionsAlertMessage = useMemo(() => {
    const witnessesRewards = get(
      find(rewardAmountsByType, { type: "poc_witnesses" }),
      "amount",
      0
    );

    const beaconingRewards = get(
      find(rewardAmountsByType, { type: "poc_challengees" }),
      "amount",
      0
    );

    if (witnessesRewards === 0 || beaconingRewards === 0) return undefined;

    const totalAmount = sumBy(rewardAmountsByType, "amount");

    return (
      <>
        <div>
          <span className="font-semibold">
            {(
              ((witnessesRewards + beaconingRewards) / totalAmount) *
              100
            ).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
            %
          </span>{" "}
          of the rewards are exposed to reward reductions.{" "}
          <span className="font-semibold">
            {((witnessesRewards / totalAmount) * 100).toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }
            )}
            %
          </span>{" "}
          of the rewards come from witnessing other hotspots. Those rewards are
          reduced based on the transmit scales of the transmitting hotspots.
        </div>

        {!isEmpty(hotspotsByWorstTransmitScale) && (
          <button
            type="button"
            onClick={() => {
              const hotspotWithWorstTransmitScale = head(
                hotspotsByWorstTransmitScale
              ) as Hotspot;
              history.push(
                `/hotspots/${hotspotWithWorstTransmitScale.id}/reward-scaling`
              );
            }}
            className="mt-4 flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400"
          >
            <span>Go to hotspot with worst transmit scale</span>
            <ChevronRightIcon className="h-5 w-5 ml-2" />
          </button>
        )}
      </>
    );
  }, [history, hotspotsByWorstTransmitScale, rewardAmountsByType]);

  const prevHotspots = usePrevious(hotspots);
  useEffect(() => {
    if (!fetchedRewards.current || prevHotspots.length !== hotspots.length) {
      fetchRewardsHistory(map(hotspots, "id"), {
        historyRewardsWithoutToday,
        singleHotspot: false
      });
      fetchedRewards.current = true;
    }
  }, [
    fetchRewardsHistory,
    historyRewardsWithoutToday,
    hotspots,
    prevHotspots.length
  ]);

  const offlineHotspots = useMemo(
    () => hotspots.filter((hotspot) => !hotspot.online),
    [hotspots]
  );

  const rewardsHistory = useMemo(() => {
    const hotspotIdsWithRewards = map(hotspots, "id").filter(
      (hotspotId) => !isEmpty(hotspotRewardsHistory[hotspotId])
    );

    if (isEmpty(hotspotIdsWithRewards)) return [];

    return zipWith(
      ...hotspotIdsWithRewards.map(getRewardsHistoryChartData),
      (...args) => ({
        timestamp: head(args)?.timestamp,
        total: sumBy(args, "total"),
        sma7: sumBy(args, "sma7")
      })
    );
  }, [hotspots, getRewardsHistoryChartData, hotspotRewardsHistory]);

  const getRewardsValue = useCallback(
    (rewardsSummaryField: string) => (hotspot: Hotspot) =>
      get(hotspotRewardsSummary, [hotspot.id, rewardsSummaryField]) || 0,
    [hotspotRewardsSummary]
  );

  const isRewardSummariesDataLoading = useMemo(
    () =>
      isEmpty(hotspots) ||
      !hotspotRewardsSummary[(head(hotspots) as Hotspot)?.id],
    [hotspotRewardsSummary, hotspots]
  );

  const isRewardsHistoryDataLoading = useMemo(
    () =>
      isEmpty(hotspots) ||
      !hotspotRewardsHistory[(head(hotspots) as Hotspot)?.id],
    [hotspotRewardsHistory, hotspots]
  );

  const meanTransmitScale = useMemo(
    () =>
      mean(
        hotspots
          .map((hotspot) => hotspot.reward_scale || 0)
          .filter((transmitScale) => !!transmitScale)
      ),
    [hotspots]
  );

  const meanWitnessRewardUnits = useMemo(
    () => meanBy(hotspots, "rx_reward_unit"),
    [hotspots]
  );

  const minBlockHeightForInteractivity = useMemo(
    () =>
      !chainVars?.hip17InteractivityBlocks || !stats?.blocks
        ? undefined
        : stats.blocks - chainVars.hip17InteractivityBlocks,
    [chainVars?.hip17InteractivityBlocks, stats?.blocks]
  );

  const activeInstallPlacementDistancesByHotspotId = useMemo(
    () =>
      values(installs)
        .filter(isActiveInstall)
        .reduce(
          (
            acc: { [hotspotId: string]: number },
            { location_uuid, hotspot_uuid }
          ) => {
            const location = locations[location_uuid];

            const hotspotCoordinates = getHotspotCoordinates(hotspot_uuid);

            if (hotspotCoordinates && location) {
              acc[hotspot_uuid] = pointDistanceMemoized(
                [hotspotCoordinates.lng, hotspotCoordinates.lat],
                [location.lng, location.lat]
              );
            }

            return acc;
          },
          {}
        ),
    [getHotspotCoordinates, installs, locations]
  );

  const attentionLists = useMemo(
    (): List[] => [
      {
        key: "inactive",
        title: "inactive",
        sortOrder: { iteratee: "name", order: "asc" },
        filter: ({ hotspot }) => !hotspot.online,
        getUrl: getHotspotSectionUrl("status")
      },
      {
        key: "active-non-interactive",
        title: "active but inactive transmit scale",
        sortOrder: { iteratee: "name", order: "asc" },
        filter: ({ hotspot }) =>
          hotspot.online &&
          !calculateIsInteractiveHotspot(
            hotspot.last_poc_challenge,
            minBlockHeightForInteractivity
          ),
        getUrl: getHotspotSectionUrl("reward-scaling")
      },
      {
        key: "terrible-and-poor-transmit-scale",
        title: "with terrible and poor transmit scales",
        sortOrder: {
          iteratee: "name",
          order: "asc"
        },
        valueDisplay: { type: "transmitScale" },
        filter: ({ hotspot }) => {
          const classification = getClassification(hotspot.reward_scale || 0);
          return ["terrible", "poor"].includes(classification);
        },
        getUrl: getHotspotSectionUrl("reward-scaling")
      },
      {
        key: "disabled-transmit-scale",
        title: "disabled for transmit scaling",
        sortOrder: {
          iteratee: "name",
          order: "asc"
        },
        filter: ({ hotspot }) => !isTransmitScaleActive(hotspot.id),
        getUrl: getHotspotSectionUrl("reward-scaling")
      },
      {
        key: "messages-but-unverified",
        title: "with received messages but unverified",
        sortOrder: {
          iteratee: "total_messages",
          order: "desc"
        },
        valueDisplay: { type: "messages" },
        filter: ({ hotspot }) =>
          isPhpHotspot(hotspot) &&
          hotspot.total_messages > 0 &&
          !hotspot.verified_by,
        getUrl: getHotspotSectionUrl("owner")
      },
      {
        key: "installation-height-not-set",
        title: "without height set",
        sortOrder: {
          iteratee: "name",
          order: "asc"
        },
        filter: ({ hotspot }) => hotspot.elevation === 0,
        getUrl: getHotspotSectionUrl("info")
      },
      {
        key: "antenna-gain-not-set",
        title: "without antenna gain set",
        sortOrder: {
          iteratee: "name",
          order: "asc"
        },
        filter: ({ hotspot }) =>
          hotspot.gain === 12 &&
          !!hotspot.payer &&
          [
            HotspotMakerAddressKey.heliumOG,
            HotspotMakerAddressKey.heliumInc,
            HotspotMakerAddressKey.calChipConnectedDevices,
            HotspotMakerAddressKey.rakWireless
          ].includes(hotspot.payer as any),
        getUrl: getHotspotSectionUrl("info")
      },
      {
        key: "placement-distance",
        title: `placed over ${renderDistance(
          PLACEMENT_DISTANCE_LIMIT,
          false
        )} away`,
        sortOrder: {
          iteratee: "name",
          order: "asc"
        },
        valueDisplay: { type: "placementDistance" },
        filter: ({ hotspot }) => {
          const distance = get(
            activeInstallPlacementDistancesByHotspotId,
            hotspot.id,
            0
          );
          return distance > PLACEMENT_DISTANCE_LIMIT;
        },
        getUrl: (hotspot) => {
          const linkedActiveHotspotInstall = find(
            values(installs).filter(isActiveInstall),
            { hotspot_uuid: hotspot.id }
          );
          return `/installs/${linkedActiveHotspotInstall?.uuid}`;
        }
      },
      {
        key: "invalid-witnesses-ratio",
        title: "with over 50% invalid witnesses",
        sortOrder: {
          iteratee: "name", // TODO: filter by the value
          order: "asc"
        },
        valueDisplay: { type: "invalidWitnessesRatio" },
        filter: ({ hotspot }) => {
          const invalidWitnessesRatio = get(
            hotspotWitnessesCountsMap,
            [hotspot.id, "invalidWitnessesRatio"],
            0
          );
          return invalidWitnessesRatio > INVALID_WITNESSES_RATIO_THRESHOLD;
        },
        getUrl: getHotspotSectionUrl("witnesses/witnessed-by")
      },
      {
        key: "denylist",
        title: "in the denylist",
        tooltip: (
          <>
            Read more about the denylist
            <ExternalLink
              className="inline-flex text-xs ml-1"
              text="here"
              url="https://github.com/helium/HIP/blob/master/0040-validator-denylist.md"
            />
          </>
        ),
        sortOrder: {
          iteratee: "name", // TODO: filter by the value
          order: "asc"
        },
        filter: ({ hotspot }) => hotspot.deny_list,
        getUrl: getHotspotSectionUrl("info")
      }
    ],
    [
      renderDistance,
      minBlockHeightForInteractivity,
      isTransmitScaleActive,
      activeInstallPlacementDistancesByHotspotId,
      installs,
      hotspotWitnessesCountsMap
    ]
  );

  const getHotspotValue = useCallback(
    (hotspot: Hotspot, iteratee: string) => {
      const iterateeFields = iteratee.split(".");

      if (iterateeFields.length > 1 && iterateeFields[0] === "rewardsSummary") {
        return get(hotspotRewardsSummary, [
          hotspot.id,
          iterateeFields.length > 1 && iterateeFields[0] === "rewardsSummary"
            ? iterateeFields[1]
            : iteratee
        ]);
      }

      return get(hotspot, iteratee);
    },
    [hotspotRewardsSummary]
  );

  const renderHotspotValue = useCallback(
    (hotspot: Hotspot, valueDisplay: ValueDisplay) => {
      switch (valueDisplay.type) {
        case "currency":
          return displayInCurrency(
            getHotspotValue(hotspot, valueDisplay.iteratee) || 0,
            { needsDivision: true }
          );
        case "percentageChange":
          return (
            <PercentageChange
              percentageChange={
                getHotspotValue(hotspot, valueDisplay.iteratee) || 0
              }
            />
          );
        case "transmitScale":
          return <ClassificationValue value={hotspot.reward_scale || 0} />;
        case "messages":
          return (hotspot as PhpHotspot).total_messages || 0;
        case "placementDistance":
          return renderDistance(
            activeInstallPlacementDistancesByHotspotId[hotspot.id],
            activeInstallPlacementDistancesByHotspotId[hotspot.id] > 1000
          );
        case "invalidWitnessesRatio":
          return formatPercentChangeString(
            get(
              hotspotWitnessesCountsMap,
              [hotspot.id, "invalidWitnessesRatio"],
              0
            ) * 100
          );
        default:
          return null;
      }
    },
    [
      activeInstallPlacementDistancesByHotspotId,
      displayInCurrency,
      getHotspotValue,
      hotspotWitnessesCountsMap,
      renderDistance
    ]
  );

  const renderList = useCallback(
    ({
      key,
      title,
      sortOrder,
      filter,
      valueDisplay,
      getUrl,
      tooltip
    }: List) => {
      let filteredHotspots = hotspots.filter(
        (hotspot) =>
          !(
            filter &&
            !filter({
              hotspot,
              rewardsSummary: hotspotRewardsSummary[hotspot.id] || {
                ...defaultSummary
              },
              rewardsHistory: hotspotRewardsHistory[hotspot.id] || []
            })
          )
      );

      if (sortOrder) {
        const iterateeFields = sortOrder.iteratee.split(".");

        filteredHotspots = orderBy(
          filteredHotspots,
          [
            iterateeFields.length > 1 && iterateeFields[0] === "rewardsSummary"
              ? (hotspot) => getHotspotValue(hotspot, iterateeFields[1])
              : sortOrder.iteratee
          ],
          [sortOrder.order]
        );
      }

      if (isEmpty(filteredHotspots)) return null;

      return (
        <div
          key={key}
          className={classNames(
            "p-2",
            isMobile && "horizontal-snap-scroll-child"
          )}
        >
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-md rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt
                className="text-lg leading-6 font-medium text-gray-900 dark:text-white truncate"
                title={`${filteredHotspots.length} ${title}`}
              >
                {filteredHotspots.length} {title}{" "}
                {tooltip && (
                  <Tooltip overlay={tooltip}>
                    <InfoIcon className="inline-flex h-4 w-4" />
                  </Tooltip>
                )}
              </dt>

              <dd
                className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white overflow-y-auto"
                style={{ maxHeight: "364px" }}
              >
                <div className="mt-6 flow-root">
                  <ul className="divide-y divide-gray-200 dark:divide-gray-500">
                    {filteredHotspots
                      .slice(
                        0,
                        showAll[key]
                          ? filteredHotspots.length + 1
                          : defaultNumberOfListItems
                      )
                      .map((hotspot) => (
                        <li key={hotspot.id} className="py-4">
                          <Link
                            className="relative flex items-center space-x-3 group"
                            to={getUrl(hotspot)}
                          >
                            <div className="flex-shrink-0">
                              <span className="inline-block relative">
                                <img
                                  className="h-10 w-10 rounded-full"
                                  src={stringifyUrl({
                                    url: `${avatarsApi}/bottts/${hotspot.id}.svg`,
                                    query: { w: 40, h: 40 }
                                  })}
                                  alt="Hotspot avatar"
                                />
                                <StatusDot
                                  hotspot={hotspot}
                                  withoutCustomRewardScaling
                                  className="absolute top-0 right-0 block"
                                  withoutTooltip
                                />
                              </span>
                            </div>
                            <div className="flex-1 min-w-0 focus:outline-none">
                              <span
                                className="absolute inset-0"
                                aria-hidden="true"
                              />
                              <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline truncate">
                                {formatHotspotName(hotspot)}
                              </p>
                              <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
                                {formatHotspotPlace(hotspot?.place)}
                              </p>
                              <DisplayLabels
                                linkedUuid={hotspot.id}
                                size="small"
                              />
                            </div>
                            {valueDisplay && (
                              <div className="text-sm text-gray-500">
                                {renderHotspotValue(hotspot, valueDisplay)}
                              </div>
                            )}
                          </Link>
                        </li>
                      ))}
                  </ul>
                </div>

                {filteredHotspots.length > defaultNumberOfListItems && (
                  <button
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 focus:outline-none dark:bg-transparent dark:hover:bg-transparent dark:text-gray-400 dark:hover:text-gray-300 dark:border-gray-500 dark:hover:border-gray-300"
                    onClick={() => {
                      setShowAll((prevShowAll) => ({
                        ...prevShowAll,
                        [key]: !prevShowAll[key]
                      }));
                    }}
                  >
                    Show {showAll[key] ? "less" : "all"}
                  </button>
                )}
              </dd>
            </div>
          </div>
        </div>
      );
    },
    [
      getHotspotValue,
      hotspotRewardsHistory,
      hotspotRewardsSummary,
      isMobile,
      hotspots,
      renderHotspotValue,
      showAll
    ]
  );

  return (
    <>
      <section className="mt-6">
        <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                Hotspots
              </dt>

              <dd className="flex items-baseline">
                <DotsFallback
                  className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white"
                  loading={isEmpty(hotspots)}
                >
                  {hotspots.length}
                </DotsFallback>

                {!isEmpty(offlineHotspots) && (
                  <div className="ml-2 flex items-baseline text-sm text-gray-500 dark:text-gray-400">
                    {offlineHotspots.length} inactive
                  </div>
                )}
              </dd>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 flex items-center justify-between">
                <span className="truncate">
                  <span className="capitalize mr-1">
                    {getClassification(meanTransmitScale)}
                  </span>
                  Transmit Scale
                  <Tooltip
                    placement="top"
                    overlay={
                      <>
                        <ExternalLink
                          className="inline-flex text-xs mr-1"
                          text="HIP17"
                          url="https://github.com/helium/HIP/blob/master/0017-hex-density-based-transmit-reward-scaling.md"
                        />
                        rewards hotspots transmitting from a given location
                        based on how many other hotspots can transmit from the
                        same area. It assigns a transmit scale which gets lower
                        the more saturated the area is so that a redundancy is
                        met but oversaturation is disincentivized. PoC rewards
                        are multiplied by the transmit scale of the transmitting
                        hotspot.
                      </>
                    }
                  >
                    <InfoIcon className="inline-flex h-4 w-4 ml-1" />
                  </Tooltip>
                </span>
              </dt>

              <DotsFallback
                className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white"
                as="dd"
                loading={isEmpty(hotspots)}
              >
                <ClassificationValue
                  value={meanTransmitScale}
                  hexProps={{
                    width: 20,
                    height: 22.8
                  }}
                />
              </DotsFallback>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 flex items-center justify-between">
                <span className="truncate">
                  <span className="capitalize mr-1">
                    {getClassification(meanWitnessRewardUnits)}
                  </span>
                  Witness Reward Units
                  <Tooltip
                    placement="top"
                    overlay={
                      <>
                        This represents the number of reward units another
                        hotspot gets for witnessing this hotspot. This value
                        ranges from 0.12 to 1 and is calculated by the 'Reward
                        Formula for each Receiver' in{" "}
                        <ExternalLink
                          className="inline-flex text-xs mr-1"
                          text="HIP15"
                          url="https://github.com/helium/HIP/blob/master/0015-beaconing-rewards.md"
                        />
                        . If this value is below 1, it means that this hotspot
                        can be witnessed by more than {hip15WitnessRedundancy}{" "}
                        other hotspots, which is the redundancy targeted by
                        Helium.
                      </>
                    }
                  >
                    <InfoIcon className="inline-flex h-4 w-4 ml-1" />
                  </Tooltip>
                </span>
              </dt>

              <dd className="flex items-baseline">
                <DotsFallback
                  className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white"
                  loading={
                    isEmpty(hotspots) || isEmpty(hotspotWitnessesCountsMap)
                  }
                >
                  <ClassificationValue
                    value={meanWitnessRewardUnits}
                    hexProps={{
                      width: 20,
                      height: 22.8
                    }}
                    withoutTransmitScaleTooltip
                  />
                </DotsFallback>
              </dd>
            </div>
          </div>
        </dl>
      </section>

      <section className="mt-6">
        <div className="pb-5 border-b border-gray-200 dark:border-gray-500">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Rewards
          </h3>
        </div>

        <div className="mt-6">
          <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                  Past 24h
                </dt>

                <DotsFallback
                  className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white"
                  as="dd"
                  loading={isRewardSummariesDataLoading}
                >
                  {displayInCurrency(
                    sum(hotspots.map(getRewardsValue("day"))),
                    { needsDivision: true }
                  )}
                  <Tooltip overlay="Percentage change of the rewards for the last 24h compared to the daily rewards averaged over the past week">
                    <PercentageChange
                      percentageChange={calculatePercentChange(
                        sum(hotspots.map(getRewardsValue("day"))),
                        sum(hotspots.map(getRewardsValue("dayWeekAverage")))
                      )}
                    />
                  </Tooltip>
                </DotsFallback>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                  Daily avg past week
                </dt>

                <DotsFallback
                  className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white"
                  as="dd"
                  loading={isRewardSummariesDataLoading}
                >
                  {displayInCurrency(
                    sum(hotspots.map(getRewardsValue("dayWeekAverage"))),
                    { needsDivision: true }
                  )}
                  <Tooltip overlay="Percentage change of the daily rewards' weekly average over the previous week's daily average">
                    <PercentageChange
                      percentageChange={calculatePercentChange(
                        sum(hotspots.map(getRewardsValue("dayWeekAverage"))),
                        sum(
                          hotspots.map(
                            getRewardsValue("dayPreviousWeekAverage")
                          )
                        )
                      )}
                    />
                  </Tooltip>
                </DotsFallback>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                  Daily avg / hotspot
                </dt>

                <DotsFallback
                  className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white"
                  as="dd"
                  loading={isRewardSummariesDataLoading}
                >
                  {displayInCurrency(
                    mean(hotspots.map(getRewardsValue("day"))),
                    { needsDivision: true }
                  )}
                  <Tooltip overlay="Percentage change of the average rewards per hotspot for the last 24h compared to the daily hotspot rewards averaged over the past week">
                    <PercentageChange
                      percentageChange={calculatePercentChange(
                        mean(hotspots.map(getRewardsValue("day"))),
                        mean(hotspots.map(getRewardsValue("dayWeekAverage")))
                      )}
                    />
                  </Tooltip>
                </DotsFallback>
              </div>
            </div>

            {withExtraRewardSummaries && (
              <>
                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                      {CURRENT_CALENDAR_MONTH_DISPLAY}
                    </dt>

                    <DotsFallback
                      className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white"
                      as="dd"
                      loading={isRewardSummariesDataLoading}
                    >
                      {displayInCurrency(
                        sum(
                          hotspots.map(getRewardsValue("currentCalendarMonth"))
                        ),
                        { needsDivision: true }
                      )}
                    </DotsFallback>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                      {PREVIOUS_CALENDAR_MONTH_DISPLAY}
                    </dt>

                    <DotsFallback
                      className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white"
                      as="dd"
                      loading={isRewardSummariesDataLoading}
                    >
                      {displayInCurrency(
                        sum(
                          hotspots.map(getRewardsValue("previousCalendarMonth"))
                        ),
                        { needsDivision: true }
                      )}
                    </DotsFallback>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                      Lifetime
                    </dt>

                    <DotsFallback
                      className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white"
                      as="dd"
                      loading={isRewardSummariesDataLoading}
                    >
                      {displayInCurrency(
                        sum(hotspots.map(getRewardsValue("lifetime"))),
                        { needsDivision: true }
                      )}
                    </DotsFallback>
                  </div>
                </div>
              </>
            )}
          </dl>
        </div>

        <div className="mt-6">
          <dl className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <div
              className={classNames(
                "bg-white dark:bg-gray-800 shadow rounded-lg",
                withRewardsByType ? "md:col-span-2" : "md:col-span-3"
              )}
            >
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 flex items-center">
                  <div className="flex-1 flex items-center">
                    <span className="truncate">Rewards by day</span>
                  </div>
                  <DataPeriodSelector
                    className="min-w-max"
                    showLifetimeOption={false}
                  />
                </dt>

                <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                  {!isRewardsHistoryDataLoading ? (
                    <div className="w-full h-52">
                      <ResponsiveContainer>
                        <ComposedChart data={rewardsHistory}>
                          <Bar dataKey="total" fill="#a96ceb" />
                          <Line
                            type="monotone"
                            dataKey="sma7"
                            stroke="#fcc945"
                            strokeWidth={2.5}
                            dot={false}
                          />
                          <ChartTooltip
                            content={HistoricalDataChartTooltipWithCurrency}
                            cursor={false}
                            isAnimationActive={false}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <CenteredLoading className="h-52" />
                  )}
                </dd>
              </div>
            </div>

            {withRewardsByType && (
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-300">
                    <ExternalLink
                      className="inline-flex"
                      text="Rewards by type"
                      url="https://docs.helium.com/blockchain/mining"
                    />

                    {!!(
                      rewardsByTypeFirstTimestamp && rewardsByTypeLastTimestamp
                    ) && (
                      <Tooltip
                        overlay={`This shows the composition of rewards starting from ${moment
                          .unix(rewardsByTypeFirstTimestamp)
                          .utc()
                          .fromNow()} until ${moment
                          .unix(rewardsByTypeLastTimestamp)
                          .utc()
                          .fromNow()}`}
                      >
                        <InfoIcon className="inline-flex h-4 w-4" />
                      </Tooltip>
                    )}
                  </dt>

                  <dd className="mt-4">
                    {!currentWorkspace?.permissions["rewards-by-type"] ? (
                      <span className="text-sm">
                        View a breakdown of the rewards by type for this hotspot
                        by upgrading to the <b>Pro</b> or <b>Business</b> plan.{" "}
                        <NavLink
                          to="/pricing"
                          className="font-bold text-hotspotty-300 hover:underline"
                        >
                          Click here
                        </NavLink>{" "}
                        to upgrade.
                      </span>
                    ) : !isEmpty(hotspotRewardsByTypeMap) ? (
                      <DonutChart
                        className="mx-auto"
                        data={rewardsByTypeChartData}
                        sizeInPx={240}
                      />
                    ) : (
                      <CenteredLoading className="h-60" />
                    )}
                  </dd>
                </div>
              </div>
            )}
          </dl>

          {rewardReductionsAlertMessage && (
            <AlertMessage
              className="mt-4"
              type="info"
              title="Reward reductions"
              message={rewardReductionsAlertMessage}
              collapsible
            />
          )}
        </div>
      </section>

      <section className="mt-6">
        <div className="pb-5 border-b border-gray-200 dark:border-gray-500">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Attention lists
          </h3>
        </div>

        <dl
          className={`mt-5 mb-6 ${
            isMobile
              ? "horizontal-snap-scroll"
              : `grid grid-cols-1 gap-5 sm:grid-cols-${gridColsInDesktop}`
          }`}
        >
          {attentionLists.map(renderList)}
        </dl>
      </section>
    </>
  );
};

export default Dashboard;

//
// Utils
//

const defaultNumberOfListItems = 3;

type ValueDisplay =
  | {
      type: "currency" | "percentageChange";
      iteratee: string;
    }
  | {
      type: "transmitScale";
    }
  | { type: "placementDistance" }
  | { type: "invalidWitnessesRatio" }
  | { type: "messages" };

interface List {
  key: string;
  title: string;
  tooltip?: any;
  sortOrder?: {
    iteratee: string;
    order: "desc" | "asc";
  };
  filter?: (hotspotData: {
    hotspot: Hotspot;
    rewardsSummary: DataSummary;
    rewardsHistory: Data[];
  }) => boolean;
  valueDisplay?: ValueDisplay;
  getUrl: (hotspot: Hotspot) => string;
}

export const PLACEMENT_DISTANCE_LIMIT = 150;

const INVALID_WITNESSES_RATIO_THRESHOLD = 0.5;

const getHotspotSectionUrl = (section: string) => (hotspot: Hotspot) =>
  `/hotspots/${hotspot.id}/${section}`;
